/* istanbul ignore file */
/* eslint-disable @typescript-eslint/naming-convention */
import { DataMapper, HttpTransport, RequestOptions } from './transport';
import { onlyResolvesLast } from 'awesome-only-resolves-last-promise';
import { AddressId, IAddressAttributes } from '../domain/entities/Address';
import {
  IShippingResponse,
  ISelectShippingAddress,
  IBillingAddress,
  IPaymentSession,
  IVerifyPayment,
  IPrivacyPolicy,
  IMunicipalityColonies,
  IGenericAddressOptionsResponse,
  IAddressValidateResponse,
  IShippingMethodResponse,
  IOrderSummaryResponse,
  IHtmlContentResponse,
} from './IApiResponses';
import {
  ISuggestions,
  GiftCard,
  IFromBopisResponse,
  IFromShippingMethodMapping,
  INewsletterSubscribed,
  IBopisData,
  IFromBopisInfoResponse,
  IFromGetTransactionKeyResponse,
  IFromSetTransactionKeyResponse,
  ICourierPostData,
  ISetCourierResponse,
  IFromCompanyBillingDetailsResponse,
  IToCompanyBillingDetailsRequest,
  IFromGetJsonAddressesSchemaResponse,
  IFromTransactionMapping,
  IDeliveryMethodsMapping,
  IFromEstimatedDeliveryMapping,
} from './IDataMappings';
import { IConfig } from '../domain/entities/Config';
import { ICoordinates } from '../interfaces/ICoordinates';
import ObjectUtils from '../utils/ObjectUtils';
import { ILocation } from '../repositories/StoresRepository';
import Session from '../domain/entities/Session/Session';
import JsonSchemaApi, { IGetAddressSchemaParams } from './JsonSchemaApi';
import { IAddressTreeNode } from '../repositories/AddressTreeNodesRepository/AddressTreeNodesRepository';
import { IDeliveryOption2, INamedDelivery } from '../domain/entities/Transaction/Transaction';
import PAYMENT_PROVIDERS, { PAYMENT_MODES, PAYMENT_TYPE } from '../constants/PaymentProviders';
import { IShippingMethod } from '../domain/entities/ShippingMethodsCollection/ShippingMethodsCollection';
import { BACKEND_VALIDATION_TYPES, DeliveryOptions, STORE_TYPE } from '../constants/Constants';
import { HeaderObject } from '@estee/elc-universal-utils';
import SHIPPING_METHODS from '../constants/ShippingMethods';
import { formatOrderSummary } from './DataMappings';
import Routes, { ExternalRoute } from '../application/routes/Routes';
import { markDirtyTransaction } from '../repositories/TransactionRepository/dirtyTransactionHOF';
import waitUntil from 'async-wait-until';
import { IClickCollectData } from './dataMapper/clickAndCollectDataMapper';

const LOW_ENV_AUTH = 'TEST-d6ef3bd7-5152-45ec-a4a7-950837c0287a:testsecreteveryoneknows';
const AUTH = 'bc738cae-3515-493a-a2f4-ad559efaad97:ee6bd2d6832511e587f168aca0c6539d209be6a49977f3c2745366f94e128990';

interface IApiDataConfig {
  isProd: boolean;
  brandId: number;
  countryId?: number;
  baseUrl: string;
  regionId: number;
  environment: string;
}

export interface IPayload {
  url: string;
  body?: {};
  options?: RequestOptions;
  headers?: {};
  returnAsPlainText?: boolean;
  params?: URLSearchParams;
}

export interface GiftCardInput {
  number: string;
  pin: string;
}

export enum HttpMethod {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

export interface IGlobalHeaders {
  AuthorizationCredentials: string;
  'Content-Type': string;
}

export interface IMakeRequestResponse {
  data?: {};
  responseHeaders?: Headers;
}

export interface IPaymentData {
  trans_key: string;
  accepted_privacy_policy?: number;
  details?: {};
  token?: {};
  payment_type?: string;
  card_holder_name?: string;
  // For Radial only:
  card_number?: string;
  card_type?: string;
  cvv?: string;
  expires_month?: number;
  expires_year?: number;
  test?: number;
  save_card?: number;
  make_default?: number;
  cc_user_payment_method_id?: string | null;
  billing_address_id?: string;
  restrict_card_number?: number;
  trans_payment_id?: number;
  default_payment_method?: string;
  payment_option?: string;
  process_additional_actions?: boolean;
  installments?: number;
  // For 2C2P
  encryptedCardInfo?: string;
}

export interface IApiErrorElement { data: {}; key: string }

export interface IApiError { errorResponse: { data: { errors: IApiErrorElement[] } } }

interface IApiConfig {
  httpTransport: HttpTransport;
  dataMapper: DataMapper;
  session: Session;
  routes: Routes;
}

class Api {
  public httpTransport: HttpTransport;

  public dataMapper: DataMapper;

  public basePath = '/rest/api/v1/rc';

  public baseTransactionPath: string;

  public brandRegionQuery: string;

  public transId: number | null;

  public RDFUID: string;

  private isServer: boolean;

  private session: Session;

  private baseUrl: string;

  private environment: string;

  private routes: Routes;

  private jsonSchemaApi: JsonSchemaApi;

  constructor(
    config: IApiConfig,
  ) {
    this.session = config.session;
    this.httpTransport = config.httpTransport;
    this.dataMapper = config.dataMapper;
    this.baseTransactionPath = `${this.basePath}/transaction`;
    this.transId = null;
    this.routes = config.routes;
  }

  getGlobalHeaders = (): IGlobalHeaders => ({
    AuthorizationCredentials: `Basic ${btoa(this.isServer ? AUTH : LOW_ENV_AUTH)}`,
    'Content-Type': 'application/json',
  });

  setTransId(transId: number): void {
    this.transId = transId;
  }

  setup(config: IApiDataConfig): void {
    this.httpTransport.setBaseUrl(config.baseUrl);
    this.isServer = config.isProd;
    this.brandRegionQuery = `brand=${config.brandId}&region=${config.regionId}`;
    this.httpTransport.addGlobalHeaders(this.getGlobalHeaders() as HeaderObject);
    this.baseUrl = config.baseUrl;
    this.environment = config.environment;
    this.jsonSchemaApi = new JsonSchemaApi(this.dataMapper, this.baseUrl);
  }

  async makeRequest(method: HttpMethod, payload: IPayload): Promise<IMakeRequestResponse> {
    try {
      this.session.resetSession();

      const {
        url,
        params,
      } = payload;
      const stringifiedParams = new URLSearchParams(params).toString();
      const urlWithParams = `${url}${params ? (url.indexOf('?') === -1 ? '?' : '&') + stringifiedParams : ''}`;

      const response = await this.httpTransport.makeRequest(
        urlWithParams,
        method,
        payload.body, {
          cache: 'no-store',
          credentials: 'include',
          ...payload.options,
        },
        payload.headers,
      );
      return response;
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        this.routes.redirect(ExternalRoute.SIGNED_OUT, `rc_error_response_${error.status}=1`);
        await new Promise(() => {});
      }

      throw error;
    }
  }

  async getAddressSuggestions(address: IAddressAttributes): Promise<ISuggestions> {
    const body = this.dataMapper.mapRequest('toAddress', address);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.basePath}/address/validate/${BACKEND_VALIDATION_TYPES.UPS}?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromSuggestions', response.data);
  }

  @markDirtyTransaction()
  async createShippingAddress(address: IAddressAttributes, useE164PhoneStandard = false): Promise<IShippingResponse> {
    const addressData = { ...address, useE164PhoneStandard };
    const body = this.dataMapper.mapRequest('toAddress', addressData);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/shipping_address?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromWithAddress', response.data);
  }

  @markDirtyTransaction()
  async updateShippingAddress(
    address: IAddressAttributes,
    shippingAddressId: AddressId,
    useE164PhoneStandard = false,
  ): Promise<IShippingResponse> {
    const addressData = { ...address, useE164PhoneStandard };
    const body = this.dataMapper.mapRequest('toAddress', addressData);
    const response = await this.makeRequest(HttpMethod.PATCH, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/shipping_address/${shippingAddressId}?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromWithAddress', response.data);
  }

  async validateAddress(
    address: IAddressAttributes,
    validateType: string,
  ): Promise<IAddressValidateResponse> {
    const body = this.dataMapper.mapRequest('toAddress', address);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.basePath}/address/validate/${validateType}?${this.brandRegionQuery}`,
    });
    const data = this.dataMapper.mapResponse('fromAddressValidation', response.data);
    return data;
  }

  @markDirtyTransaction()
  async selectShippingAddress(shippingAddressId: AddressId): Promise<ISelectShippingAddress> {
    const response = await this.makeRequest(HttpMethod.PUT, {
      url: `${this.baseTransactionPath}/${this.transId}/shipping_address/${shippingAddressId}?${this.brandRegionQuery}`,
    });

    return response.data;
  }

  @markDirtyTransaction()
  async deleteShippingAddress(
    shippingAddressId: AddressId,
  ): Promise<void> {
    await this.makeRequest(HttpMethod.DELETE, {
      url: `${this.baseTransactionPath}/${this.transId}/shipping_address/${shippingAddressId}?${this.brandRegionQuery}`,
    });
  }

  @markDirtyTransaction()
  async createBillingAddress(address: IAddressAttributes, useE164PhoneStandard = false): Promise<IBillingAddress> {
    const addressData = { ...address, useE164PhoneStandard };
    const body = this.dataMapper.mapRequest('toAddress', addressData);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/billing_address?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromWithAddress', response.data);
  }

  @markDirtyTransaction()
  async selectBillingAddress(addressId: AddressId): Promise<IBillingAddress> {
    const response = await this.makeRequest(HttpMethod.PUT, {
      url: `${this.baseTransactionPath}/${this.transId}/billing_address/${addressId}?${this.brandRegionQuery}`,
    });

    return response.data;
  }

  async getTownsByCity(city: string): Promise<{ towns: string[] }> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.basePath}/util/citytowndistrict/${city}?${this.brandRegionQuery}`,
    });
    return response.data as { towns: string[] };
  }

  async getDistrictsByCityTown({
    city,
    town,
  }: {
    city: string;
    town: string;
  }): Promise<{ districts: string[] }> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.basePath}/util/citytowndistrict/${city}/${town}?${this.brandRegionQuery}`,
    });

    return response.data as { districts: string[] };
  }

  async getStreetsAndPostalCodeByCityTownDistrict({
    city,
    town,
    district,
  }: {
    city: string;
    town: string;
    district: string;
  }): Promise<{ postal_codes: string[]; streets: string[] }> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.basePath}/util/citytowndistrict/${city}/${town}/${district}?${this.brandRegionQuery}`,
    });

    return response.data as { postal_codes: string[]; streets: string[] };
  }

  async getMunicipalityandColoniesByZip(zipcode: string): Promise<IMunicipalityColonies> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.basePath}/util/muncipalitycolonies/${zipcode}?${this.brandRegionQuery}`,
    });
    return response.data as IMunicipalityColonies;
  }

  // used only for local development
  async getDataConfig(): Promise<IConfig> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.basePath}/util/dataconfig/?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromSiteConfig', response.data) as IConfig;
  }

  async getTransaction(countryId?: number): Promise<IFromTransactionMapping> {
    const countryQuery = countryId ? `&country_id=${countryId}` : '';
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.baseTransactionPath}?${this.brandRegionQuery}${countryQuery}`,
    });
    return this.dataMapper.mapResponse('fromTransaction', response.data) as IFromTransactionMapping;
  }

  getTransactionDebounced = onlyResolvesLast(this.getTransaction.bind(this)) as (countryId?: number) => Promise<IFromTransactionMapping>;

  async getEstimatedDeliveryDate(
    address: IAddressAttributes,
  ): Promise<IFromEstimatedDeliveryMapping> {
    const body = this.dataMapper.mapRequest('toAddress', address);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/estimateddelivery?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromEstimatedDelivery', response.data) as IFromEstimatedDeliveryMapping;
  }

  // Check if the DOM RDFUID element exists, if not means that payment requests
  // don't need this attribute so we can avoid adding it.
  // Then wait for RDFUID value to be present, otherwise continue without it.
  // Not having the RDFUID should not block the payment.
  async getPaymentRDFUID(): Promise<string> {
    const domRDFUID = document.getElementById('RDFUID');
    if (!domRDFUID) return '';

    if (!this.RDFUID) {
      try {
        this.RDFUID = await waitUntil(() => domRDFUID.getAttribute('value'), 5000);
      } catch {
        return '';
      }
    }

    return `&RDFUID=${this.RDFUID}`;
  }

  async getPaymentSession(): Promise<IPaymentSession> {
    const RDFUIDQuery = await this.getPaymentRDFUID();
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.baseTransactionPath}/payment?${this.brandRegionQuery}${RDFUIDQuery}`,
    });
    return (response.data as IPaymentSession[])?.[0];
  }

  async verifyPayment(paymentData: IPaymentData): Promise<IVerifyPayment> {
    const RDFUIDQuery = await this.getPaymentRDFUID();
    const response = await this.makeRequest(HttpMethod.POST, {
      body: paymentData,
      url: `${this.baseTransactionPath}/payment?${this.brandRegionQuery}${RDFUIDQuery}`,
    });
    return response.data;
  }

  async createPaymentSession(
    paymentProvider: PAYMENT_PROVIDERS,
    paymentType: PAYMENT_TYPE = PAYMENT_TYPE.PP,
    paymentMode?: PAYMENT_MODES)
    : Promise<IPaymentSession> {
    const RDFUIDQuery = await this.getPaymentRDFUID();

    let url = `${this.baseTransactionPath}/payment/${paymentType}_${paymentProvider}?${this.brandRegionQuery}${RDFUIDQuery}`;
    url = paymentMode ? url = `${url}&payment_mode=${paymentMode}` : url;

    const response = await this.makeRequest(HttpMethod.PUT, {
      url,
    });

    return (response.data as IPaymentSession[])?.[0];
  }

  createPaymentSessionDebounced = onlyResolvesLast(this.createPaymentSession.bind(this));

  async addGiftCard(giftcard: GiftCardInput): Promise<GiftCard> {
    const response = await this.makeRequest(HttpMethod.PATCH, {
      body: {
        gc_number: giftcard.number,
        gc_pin: giftcard.pin,
      },
      url: `${this.baseTransactionPath}/payment/GC_GiftCard?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromAddGiftCard', response.data) as GiftCard;
  }

  async removeGiftCard(id: number): Promise<{}> {
    const response = await this.makeRequest(HttpMethod.DELETE, {
      body: {},
      url: `${this.baseTransactionPath}/payment/id/${id}?${this.brandRegionQuery}`,
    });

    return response.data;
  }

  @markDirtyTransaction()
  async setShippingMethod(
    shippingMethodId: SHIPPING_METHODS,
    { deliveryDate, deliveryTime }: INamedDelivery,
    zipCode?: string,
    countryId?: number,
  ): Promise<IFromShippingMethodMapping> {
    const body: { named_delivery_date_options?: {}, zip_code?: string } = {};
    if (deliveryDate || deliveryTime) {
      body.named_delivery_date_options = {
        delivery_date: deliveryDate,
        delivery_time: deliveryTime,
      };
    }
    if (zipCode) {
      body.zip_code = zipCode;
    }
    const countryIdQuery = countryId ? `&country_id=${countryId}` : '';
    const response = await this.makeRequest(HttpMethod.PUT, {
      body,
      options: {
        credentials: 'include',
      },
      url: `${this.baseTransactionPath}/${this.transId}/ship_method/${shippingMethodId}?${this.brandRegionQuery}${countryIdQuery}`,
    });
    return this.dataMapper.mapResponse('fromShipMethod', response.data) as IFromShippingMethodMapping;
  }

  setShippingMethodDebounced = onlyResolvesLast(this.setShippingMethod.bind(this));

  async getShippingMethods(
    countryId: number,
    useDeliveryData = false,
  ): Promise<IShippingMethod[]> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.baseTransactionPath}/${this.transId}/ship_methods/?${this.brandRegionQuery}&country_id=${countryId}`,
    });

    if (useDeliveryData) {
      const deliveryOptions = this.dataMapper.mapResponse('fromDeliveryOptions', response.data) as IDeliveryOption2[];
      return deliveryOptions?.find(delivery => delivery.name === DeliveryOptions.HOME_DELIVERY).shipMethods || [];
    } else {
      return this.dataMapper.mapResponse('fromShipMethods', response.data) as IShippingMethod[];
    }
  }

  async getSubscription(): Promise<INewsletterSubscribed> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.baseTransactionPath}/newsletter?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromSubscription', response.data) as INewsletterSubscribed;
  }

  async setSubscription(value: number): Promise<INewsletterSubscribed> {
    const response = await this.makeRequest(HttpMethod.PUT, {
      url: `${this.baseTransactionPath}/newsletter/${value}?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromSubscription', response.data) as INewsletterSubscribed;
  }

  async setAcceptedPrivacyPolicy(value: number): Promise<IPrivacyPolicy> {
    const response = await this.makeRequest(HttpMethod.PUT, {
      url: `${this.basePath}/user/accepted_privacy_policy/${value}?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromAcceptedPrivacyPolicy', response.data);
  }

  async getSuggestionAddressFromZip(zipcode: string, zipcodeApiPath?: string): Promise<ISuggestions> {
    let url;
    if (zipcodeApiPath) {
      url = `${this.basePath}${zipcodeApiPath}/${zipcode.replace(/\D/g, '')}?${this.brandRegionQuery}`;
    } else {
      url = `${this.basePath}/util/postalcodeinfo/${zipcode}?${this.brandRegionQuery}`;
    }
    const response = await this.makeRequest(HttpMethod.GET, {
      options: {
        credentials: 'include',
      },
      url,
    });
    return this.dataMapper.mapResponse('fromGetSuggestionAddresses', response.data);
  }

  async getBopisStoresByGeolocation(coords: ICoordinates, isClickAndCollect: boolean, storeType: number): Promise<IFromBopisResponse> {
    const params = new URLSearchParams({
      courier: '1',
      latitude: String(coords.latitude),
      longitude: String(coords.longitude),
      mode: 'coords',
    });
    const storesParams = storeType ? `&stores=${STORE_TYPE.STORE_COLLECTION}` : '';
    const response = await this.makeRequest(HttpMethod.GET, {
      options: {
        credentials: 'include',
      },
      params,
      url: `${this.basePath}${isClickAndCollect ? '/transaction/store_collection?' : '/bopis/inventory?'}${this.brandRegionQuery}${storesParams}`,
    });
    return this.dataMapper.mapResponse('fromGetBopisToStores', response.data) as IFromBopisResponse;
  }

  async getStoreById(doorId: string): Promise<IFromBopisResponse> {
    const params = new URLSearchParams({
      door_ids: doorId,
      mode: 'door_id',
    });

    const response = await this.makeRequest(HttpMethod.GET, {
      options: {
        credentials: 'include',
      },
      params,
      url: `${this.basePath}/bopis/inventory?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromGetBopisToStores', response.data) as IFromBopisResponse;
  }

  async getCourierStoresByAddress(location: ILocation): Promise<IFromBopisResponse> {
    const params = new URLSearchParams(ObjectUtils.removeUndefinedProps({
      city: location.city,
      courier: '1',
      mode: 'address',
      state: location.state,
      zip: location.zip,
    }));

    const response = await this.makeRequest(HttpMethod.GET, {
      params,
      url: `${this.basePath}/bopis/inventory?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromGetBopisToStores', response.data) as IFromBopisResponse;
  }

  async getCourierStoresByIP(): Promise<IFromBopisResponse> {
    const params = new URLSearchParams(ObjectUtils.removeUndefinedProps({
      courier: '1',
      mode: 'use_header',
    }));

    const response = await this.makeRequest(HttpMethod.GET, {
      params,
      url: `${this.basePath}/bopis/inventory?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromGetBopisToStores', response.data) as IFromBopisResponse;
  }

  async getClickAndCollectStoresByIP(storeType: number): Promise<IFromBopisResponse> {
    const params = new URLSearchParams(ObjectUtils.removeUndefinedProps({
      courier: '1',
      mode: 'use_header',
      stores: storeType ? STORE_TYPE.STORE_COLLECTION : undefined,
    }));

    const response = await this.makeRequest(HttpMethod.GET, {
      params,
      url: `${this.basePath}/transaction/store_collection?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromGetBopisToStores', response.data) as IFromBopisResponse;
  }

  @markDirtyTransaction()
  async setClickAndCollectStoreAddress(data: IClickCollectData): Promise<IShippingResponse> {
    const body = this.dataMapper.mapRequest('clickAndCollectDataMapper', data);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/shipping_address?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromWithAddress', response.data);
  }

  @markDirtyTransaction()
  async updateClickAndCollectStoreAddress(
    data: IClickCollectData,
    shippingAddressId: AddressId,
  ): Promise<IShippingResponse> {
    const body = this.dataMapper.mapRequest('clickAndCollectDataMapper', data);
    const response = await this.makeRequest(HttpMethod.PATCH, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/shipping_address/${shippingAddressId}?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromWithAddress', response.data);
  }

  @markDirtyTransaction()
  async setBopis(info: IBopisData): Promise<IFromBopisInfoResponse> {
    const body = this.dataMapper.mapRequest('toBopisInfo', info);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/bopis/select?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromBopisInfo', response.data) as IFromBopisInfoResponse;
  }

  async removeItemsUnavailableForStore(doorId: string): Promise<IFromBopisInfoResponse> {
    const body = {
      door_id: doorId,
      door_mode: 'set',
      no_address_delete: 0,
      no_cart_delete: 0,
      proxy_pickup_mode: 'unset',
      purchaser_pickup_mode: 'unset',
    };

    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/bopis/select?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromBopisInfo', response.data) as IFromBopisInfoResponse;
  }

  @markDirtyTransaction()
  async setCourier(info: ICourierPostData): Promise<ISetCourierResponse> {
    const body = this.dataMapper.mapRequest('toCourierInfo', info);
    const response = await this.makeRequest(HttpMethod.POST, {
      body,
      url: `${this.baseTransactionPath}/${this.transId}/bopis/select?${this.brandRegionQuery}&courier=1`,
    });

    return this.dataMapper.mapResponse('fromSetCourier', response.data) as ISetCourierResponse;
  }

  async removeBopis(): Promise<IFromBopisInfoResponse> {
    const response = await this.makeRequest(HttpMethod.POST, {
      body: {
        door_mode: 'unset',
      },
      url: `${this.baseTransactionPath}/${this.transId}/bopis/select?${this.brandRegionQuery}`,
    });

    return this.dataMapper.mapResponse('fromBopisInfo', response.data) as IFromBopisInfoResponse;
  }

  async getTransactionKey(): Promise<IFromGetTransactionKeyResponse> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.baseTransactionPath}/${this.transId}/validation?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromGetTransactionKey', response.data) as IFromGetTransactionKeyResponse;
  }

  async setTransactionKey(transKey: string): Promise<IFromSetTransactionKeyResponse> {
    const response = await this.makeRequest(HttpMethod.POST, {
      body: {
        trans_key: transKey,
      },
      url: `${this.baseTransactionPath}/${this.transId}/validation?${this.brandRegionQuery}`,
    });
    return this.dataMapper.mapResponse('fromSetTransactionKey', response.data) as IFromSetTransactionKeyResponse;
  }

  @markDirtyTransaction()
  async updateCompanyBillingDetails(
    companyBillingDetails: IFromCompanyBillingDetailsResponse,
  ): Promise<IFromCompanyBillingDetailsResponse> {
    const body = this.dataMapper.mapRequest(
      'toCompanyBillingDetailsRequest',
      companyBillingDetails,
    );
    const response = await this.makeRequest(HttpMethod.PATCH, {
      body,
      url: `${this.basePath}/bill_to_company/${this.transId}?${this.brandRegionQuery}`,
    });
    const { bill_to_company } = response.data as IToCompanyBillingDetailsRequest;
    return this.dataMapper.mapResponse(
      'fromCompanyBillingDetailsInfo',
      bill_to_company,
    ) as IFromCompanyBillingDetailsResponse;
  }

  async getAddressJsonSchema(
    params: IGetAddressSchemaParams,
  ): Promise<IFromGetJsonAddressesSchemaResponse> {
    return this.jsonSchemaApi.getAddressSchema(params);
  }

  async getStaticJsonByPath(jsonPath: string): Promise<IAddressTreeNode[]> {
    const timestamp = Math.floor(Date.now() / 1000); 
    const response = await fetch(`${this.baseUrl}/${jsonPath}?${timestamp}`);
    return await response.json() as IAddressTreeNode[];
  }

  async getAddressOptionsFromRemote(
    addressLookupUrl: string,
  ): Promise<IGenericAddressOptionsResponse> {
    const response = await this.makeRequest(HttpMethod.GET, {
      url: `${this.basePath}/${addressLookupUrl}?${this.brandRegionQuery}`,
    });

    return response.data;
  }

  // TODO: Legacy, remove when we decide to only use Melissa for this
  async getSuggestionFromAddressSearchLegacy(keywords: string, countryId: number): Promise<{ label: string, value: string }[]> {
    const url = `${this.basePath}/util/addressinfosearch/${keywords}?${this.brandRegionQuery}&country_id=${countryId}`;
    const response = await this.makeRequest(HttpMethod.GET, {
      url,
    });
    const { suggestions } = response.data as { suggestions: { label: string, value: string }[] };
    return suggestions;
  }

  // TODO: Legacy, remove when we decide to only use Melissa for this
  async getAddressFieldsFromSelectedSuggestionValueLegacy(
    keywords: string, addressValue: string, countryId: number,
  ): Promise<IAddressAttributes> {
    const url = `${this.basePath}/util/addressinfosearch/${keywords}?address_value=${addressValue}&${this.brandRegionQuery}&country_id=${countryId}`;
    const response = await this.makeRequest(HttpMethod.GET, {
      url,
    });

    return this.dataMapper.mapResponse('toSelectedAddressFields', response.data) as IAddressAttributes;
  }

  async getDeliveryMethodsFromZip(zipcode: string): Promise<IDeliveryMethodsMapping> {
    const url = `${this.basePath}/util/shippingcalculator/${this.transId}/${zipcode}?${this.brandRegionQuery}`;
    const response = await this.makeRequest(HttpMethod.GET, {
      url,
    });
    const { ship_methods, order_summary } = response.data as { ship_methods: IShippingMethodResponse[], order_summary: IOrderSummaryResponse };
    const methods = this.dataMapper.mapResponse('deliveryMethodsMapper', ship_methods) as IShippingMethod[];
    return { deliveryMethods: methods, orderSummary: formatOrderSummary(order_summary) };
  }

  async getHtmlContent(node: number): Promise<string> {
    const url = `/elc_api/node/${node}/all/api`;
    const response = await this.makeRequest(HttpMethod.GET, {
      url,
    }) as IHtmlContentResponse;
    return response.data?.[node].template_fields.data.html.textarea.value;
  }
}

export default Api;
